import { useCallback, useEffect, useState } from 'react';
import {
  ITEM_TYPE_LABELS,
  ITEM_TYPE_ORDER,
  ITEM_TYPE_UNITS,
} from '../utils/itemTypes';

interface MemoryPanelProps {
  apiUrl: string;
  token: string | null;
  open: boolean;
  refreshKey?: number;
  onClose: () => void;
  onToast?: (msg: string, type?: 'success' | 'error' | 'info') => void;
  /** 点击标签/搜索词后发送消息（常看标签→catalog，近期搜索→search） */
  onSendMessage?: (message: string) => void;
}

interface PreferredTagItem {
  tag: string;
  item_type: string;
}

interface ProfileData {
  preferred_tag_items?: PreferredTagItem[];
  disliked_tags?: string[];
  summary?: string;
  recent_queries?: string[];
  updated_at?: string;
}

/** 常看标签 → catalog keyword + match_tags，并带上内容类型 */
function catalogMessageForTag(tag: string, itemType: string): string {
  const label = ITEM_TYPE_LABELS[itemType] || '内容';
  const unit = ITEM_TYPE_UNITS[itemType] || '部';
  return `标签含${tag}的10${unit}${label}`;
}

function groupTagsByType(
  items: PreferredTagItem[],
): { itemType: string; label: string; tags: string[] }[] {
  const map = new Map<string, string[]>();
  for (const item of items) {
    const tag = String(item?.tag || '').trim();
    const itemType = String(item?.item_type || '').trim();
    if (!tag || !itemType) continue;
    const list = map.get(itemType) || [];
    if (!list.includes(tag)) list.push(tag);
    map.set(itemType, list);
  }
  const ordered = ITEM_TYPE_ORDER.filter((t) => map.has(t));
  const rest = [...map.keys()].filter((t) => !ITEM_TYPE_ORDER.includes(t));
  return [...ordered, ...rest].map((itemType) => ({
    itemType,
    label: ITEM_TYPE_LABELS[itemType] || itemType,
    tags: map.get(itemType) || [],
  }));
}

export function MemoryPanel({
  apiUrl,
  token,
  open,
  refreshKey = 0,
  onClose,
  onToast,
  onSendMessage,
}: MemoryPanelProps) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [enabled, setEnabled] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await fetch(`${apiUrl}/v1/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 400) {
        setEnabled(false);
        setProfile(null);
        return;
      }
      if (!resp.ok) throw new Error(String(resp.status));
      const data = await resp.json();
      setEnabled(Boolean(data.memory_enabled));
      setProfile(data.profile || null);
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  const handleTagClick = (tag: string, itemType: string) => {
    if (!onSendMessage) return;
    onSendMessage(catalogMessageForTag(tag, itemType));
    onClose();
  };

  const handleQueryClick = (query: string) => {
    if (!onSendMessage) return;
    onSendMessage(query);
    onClose();
  };

  const clearMemory = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${apiUrl}/v1/profile`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok || resp.status === 204) {
        setProfile(null);
        onToast?.('已清除偏好记忆', 'success');
      }
    } catch {
      onToast?.('清除失败', 'error');
    }
  };

  if (!open) return null;

  return (
    <div className="memory-panel-overlay" role="dialog" aria-modal="true" aria-label="偏好记忆">
      <div className="memory-panel">
        <div className="memory-panel-header">
          <span>偏好记忆</span>
          <button type="button" className="header-close" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>
        <div className="memory-panel-body">
          {loading && <div className="memory-panel-empty">加载中…</div>}
          {!loading && !enabled && (
            <div className="memory-panel-empty">当前会话未绑定用户，或无长期记忆。</div>
          )}
          {!loading && enabled && !profile && (
            <div className="memory-panel-empty">暂无记录，继续聊天我会慢慢了解你的偏好。</div>
          )}
          {!loading && profile && (
            <>
              {profile.preferred_tag_items && profile.preferred_tag_items.length > 0 && (
                <div className="memory-section">
                  <div className="memory-section-title">常看标签</div>
                  {groupTagsByType(profile.preferred_tag_items).map((group) => (
                    <div key={group.itemType} className="memory-tag-group">
                      <div className="memory-tag-group-label">{group.label}</div>
                      <div className="memory-tags">
                        {group.tags.map((t) =>
                          onSendMessage ? (
                            <button
                              key={`${group.itemType}:${t}`}
                              type="button"
                              className="memory-tag memory-tag--clickable"
                              title={`按${group.label}标签浏览`}
                              onClick={() => handleTagClick(t, group.itemType)}
                            >
                              {t}
                            </button>
                          ) : (
                            <span key={`${group.itemType}:${t}`} className="memory-tag">
                              {t}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {profile.disliked_tags && profile.disliked_tags.length > 0 && (
                <div className="memory-section">
                  <div className="memory-section-title">不喜欢的标签</div>
                  <div className="memory-tags">
                    {profile.disliked_tags.map((t) => (
                      <span key={t} className="memory-tag memory-tag--disliked">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {profile.recent_queries && profile.recent_queries.length > 0 && (
                <div className="memory-section">
                  <div className="memory-section-title">近期搜索</div>
                  <div className="memory-tags">
                    {profile.recent_queries.slice(0, 8).map((q) =>
                      onSendMessage ? (
                        <button
                          key={q}
                          type="button"
                          className="memory-tag memory-tag--query memory-tag--clickable"
                          title="再次搜索"
                          onClick={() => handleQueryClick(q)}
                        >
                          {q}
                        </button>
                      ) : (
                        <span key={q} className="memory-tag memory-tag--query">
                          {q}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}
              {profile.summary && (
                <div className="memory-section">
                  <div className="memory-section-title">摘要</div>
                  <p className="memory-summary">{profile.summary}</p>
                </div>
              )}
              <button type="button" className="memory-clear-btn" onClick={clearMemory}>
                清除偏好记忆
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
